import type { JSX } from 'preact';
import { type IconName, icons } from './icons.js';

/**
 * Renders a self-hosted Material Symbols glyph inline. `style` controls size
 * (via `fontSize`, kept as the sizing knob so call sites didn't need to
 * change when this replaced the ligature-font `<span class="ms">` approach)
 * and color (via `color`, inherited by the SVG through `fill: currentColor`
 * in index.css's `.icon svg` rule).
 *
 * `name` is a plain `string` (not the `IconName` union) because it also
 * takes `Service.icon` from validated content, not just literal call-site
 * names — an unknown name renders nothing rather than throwing.
 *
 * Always `aria-hidden` — every call site in this app sits next to its own
 * visible text label (a title, a button label, a nav link) or inside a
 * control that already carries its own `aria-label` (e.g. the theme
 * toggle), so the glyph is decorative. Without this, assistive tech reads
 * the glyph's ligature name ("school", "bolt") aloud as noise on top of the
 * label that's already being read.
 */
export function Icon({ name, style }: { name: string; style?: JSX.CSSProperties }) {
  const svg = icons[name as IconName];
  if (!svg) return null;
  return (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: one of a fixed set of locally-bundled, build-time SVGs — never user input
    <span class="icon" aria-hidden="true" style={style} dangerouslySetInnerHTML={{ __html: svg }} />
  );
}
